import {CarouselClickHandleClick} from 'helpers-ga4';

if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.thumbs = null;
      this.carousel = null;

      this.init();
    }

    init() {
      const thumbsEl = this.querySelector('[data-media-gallery-thumbs]');
      const carouselEl = this.querySelector('[data-media-gallery-carousel]');
      const galleryType = this.getAttribute('data-gallery-type');
      const desktopThumbnailPosition = this.getAttribute(
        'data-desktop-thumbnail-position',
      );
      const enableDesktopDots =
        this.getAttribute('data-enable-desktop-dots') === 'true';
      const enableMobileDots =
        this.getAttribute('data-enable-mobile-dots') === 'true';
      const desktopBreakpoint =
        desktopThumbnailPosition === 'left' ? 1200 : 801;
      const enableDots =
        window.innerWidth >= desktopBreakpoint
          ? enableDesktopDots
          : enableMobileDots;
      const enableFadeTransition =
        this.getAttribute('data-enable-fade-transition') === 'true';
      const enableKeyboard =
        this.getAttribute('data-enable-keyboard') !== 'false';
      const isTabPanelCarousel =
        (window.innerWidth < desktopBreakpoint && enableMobileDots) ||
        (window.innerWidth >= desktopBreakpoint && enableDesktopDots);
      const sectionId = this.getAttribute('data-section-id');
      const self = this;

      if (!carouselEl) {
        console.warn('ProductMediaGallery: missing required elements.');
        return;
      }

      this.autoplayBackgroundVideo =
        this.getAttribute('data-enable-gallery-video-autoplay') !== 'false';
      this.hasBackgroundVideoSlides =
        !!carouselEl.querySelector('background-video');
      this.prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;

      const firstSlideImage = carouselEl.querySelector(
        '.swiper-slide:first-child img',
      );
      if (firstSlideImage) {
        const revealFirstSlide = () => self.classList.add('first-slide-loaded');
        if (firstSlideImage.complete) {
          revealFirstSlide();
        } else {
          firstSlideImage.addEventListener('load', revealFirstSlide, {
            once: true,
          });
        }
      }

      // Initialize thumbnail carousel
      if (thumbsEl) {
        const thumbsConfig = {
          spaceBetween: 6,
          slidesOffsetBefore: 6,
          slidesOffsetAfter: 6,
          slidesPerView: 'auto',
          freeMode: true,
          watchSlidesProgress: true,
          navigation: {
            nextEl: this.querySelector(
              '.swiper-button-next.swiper-button-media-gallery-thumbs',
            ),
            prevEl: this.querySelector(
              '.swiper-button-prev.swiper-button-media-gallery-thumbs',
            ),
          },
          a11y: {
            enabled: true,
            slideRole: '',
            slideLabelMessage: '',
            prevSlideMessage: 'Show previous thumbnails',
            nextSlideMessage: 'Show next thumbnails',
          },
          keyboard: {
            enabled: false,
            onlyInViewport: true,
          },
          on: {
            afterInit: function (swiper) {
              const wrapper = swiper.wrapperEl;
              if (wrapper) {
                wrapper.removeAttribute('aria-live');
              }

              const mediaGallery = document.querySelector('.media-gallery');
              if (mediaGallery) {
                mediaGallery.classList.remove('loading-thumbs');
              }

              const skeleton = document.querySelector(
                '[data-media-gallery-skeleton-thumbs]',
              );
              if (skeleton) {
                setTimeout(() => {
                  skeleton.remove();
                }, 500);
              }
            },
          },
        };

        if (galleryType === 'columns') {
          thumbsConfig.breakpoints = {
            768: {
              spaceBetween: 8,
              slidesPerView: 7.2,
            },
            801: {
              enabled: false,
            },
          };
        } else {
          const isDesktopVertical = desktopThumbnailPosition === 'left';
          const breakpointKey = isDesktopVertical ? 1200 : 801;
          thumbsConfig.breakpoints = {
            [breakpointKey]: {
              direction: isDesktopVertical ? 'vertical' : 'horizontal',
              ...(isDesktopVertical
                ? {slidesOffsetBefore: 0, slidesOffsetAfter: 0}
                : {}),
            },
          };
        }

        this.thumbs = new Swiper(thumbsEl, thumbsConfig);
        if (window.innerWidth >= 801 && galleryType === 'columns') {
          thumbsEl.classList.add('hidden');
        }
      }

      // Initialize main carousel
      const carouselConfig = {
        spaceBetween: 10,
        allowTouchMove: true,
        touchEventsTarget: 'wrapper',
        watchOverflow: true,
        slidesPerView: 1,
        ...(enableFadeTransition
          ? {effect: 'fade', fadeEffect: {crossFade: true}, speed: 800}
          : {}),
        navigation: {
          nextEl: this.querySelector(
            '.swiper-button-next.swiper-button-media-gallery-carousel',
          ),
          prevEl: this.querySelector(
            '.swiper-button-prev.swiper-button-media-gallery-carousel',
          ),
        },
        a11y: {
          enabled: true,
          slideRole: isTabPanelCarousel ? 'tabpanel' : 'group',
          slideLabelMessage: 'Slide {{index}} of {{slidesLength}}',
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
        },
        thumbs: {
          swiper: this.thumbs,
          autoScrollOffset: 1,
        },
        pagination: enableDots
          ? {
              el: this.querySelector(
                '.swiper-pagination-media-gallery-carousel',
              ),
              clickable: true,
              dynamicBullets: false,
              renderBullet: function (index, className) {
                const totalSlides = this.slides.length;
                const slideId =
                  this.slides[index]?.id || `${sectionId}-tab-${index + 1}`;
                const ariaSelected = index === 0 ? 'true' : 'false';
                const tabIndex = index === 0 ? '0' : '-1';

                return `<button id="${sectionId}-dot-${index + 1}" aria-controls="${slideId}" type="button" aria-selected="${ariaSelected}" role="tab" aria-label="Slide ${index + 1} of ${totalSlides}" tabindex="${tabIndex}" class="${className} bullet-${index}"></button>`;
              },
            }
          : false,
        keyboard: {
          enabled: enableKeyboard,
          onlyInViewport: true,
        },
        on: {
          afterInit: function (swiper) {
            self.updateGalleryBackgroundVideos(swiper);
            self.updateModalOpenerSemantics(galleryType);
            self.updateSlidesListSemantics(galleryType);

            const wrapper = swiper.wrapperEl;
            wrapper.removeAttribute('aria-live');

            setTimeout(() => {
              if (
                window.innerWidth <= 800 ||
                (window.innerWidth > 800 && galleryType === 'thumbnail_slider')
              ) {
                swiper.slides.forEach((slide, index) => {
                  const isActive = index === swiper.activeIndex;
                  if (isActive) {
                    slide.removeAttribute('aria-hidden');
                    slide.removeAttribute('inert');
                  } else {
                    slide.setAttribute('aria-hidden', 'true');
                    slide.setAttribute('inert', '');
                  }

                  const focusableElements = slide.querySelectorAll(
                    'a, button, input, select, textarea, [tabindex]',
                  );
                  focusableElements.forEach((el) => {
                    if (isActive) {
                      el.removeAttribute('tabindex');
                      el.removeAttribute('aria-hidden');
                    } else {
                      el.setAttribute('tabindex', '-1');
                      el.setAttribute('aria-hidden', 'true');
                    }
                  });
                });
              }
            }, 100);

            if (
              window.innerWidth <= 800 ||
              (window.innerWidth > 800 && galleryType === 'thumbnail_slider')
            ) {
              const gallerySection = document.querySelector(
                '[data-media-gallery-section-wrapper]',
              );
              if (gallerySection) {
                gallerySection.setAttribute('aria-roledescription', 'carousel');
              }
            }

            if (enableDots) {
              self.bindPaginationTabs(swiper);
              self.updatePaginationTabs(swiper);
            }

            const mediaGallery = document.querySelector('.media-gallery');
            if (mediaGallery) {
              mediaGallery.classList.remove('loading-main-carousel');
            }

            const skeleton = document.querySelector(
              '[data-media-gallery-skeleton-main-carousel]',
            );
            if (skeleton) {
              skeleton.classList.add('hide_skeleton');
              setTimeout(() => {
                skeleton.remove();
              }, 500);
            }

            // Initialize thumbnail tabindex based on main carousel active index
            if (swiper.thumbs && swiper.thumbs.swiper) {
              const thumbsSwiper = swiper.thumbs.swiper;
              thumbsSwiper.slides.forEach((thumbSlide, thumbIndex) => {
                const button = thumbSlide.querySelector(
                  '.media-gallery__thumbs-button',
                );
                if (button) {
                  if (thumbIndex === swiper.activeIndex) {
                    button.setAttribute('aria-current', 'true');
                  } else {
                    button.setAttribute('aria-current', 'false');
                  }

                  thumbSlide.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      swiper.slideTo(thumbIndex);
                    }
                  });

                  button.addEventListener('focus', () => {
                    self.syncThumbsNavigation(thumbsSwiper, thumbIndex);
                  });
                }
              });
            }
          },
          slideChange: function () {
            self.updateGalleryBackgroundVideos(this);

            if (enableDots) {
              self.updatePaginationTabs(this);
            }

            if (
              window.innerWidth <= 800 ||
              (window.innerWidth > 800 && galleryType === 'thumbnail_slider')
            ) {
              const swiperInstance = this;
              this.slides.forEach((slide, index) => {
                const isActive = index === this.activeIndex;
                if (isActive) {
                  slide.removeAttribute('aria-hidden');
                  slide.removeAttribute('inert');
                } else {
                  slide.setAttribute('aria-hidden', 'true');
                  slide.setAttribute('inert', '');
                }

                const focusableElements = slide.querySelectorAll(
                  'a, button, input, select, textarea, [tabindex]',
                );
                focusableElements.forEach((el) => {
                  if (isActive) {
                    el.removeAttribute('tabindex');
                    el.removeAttribute('aria-hidden');
                  } else {
                    el.setAttribute('tabindex', '-1');
                    el.setAttribute('aria-hidden', 'true');
                  }
                });
              });

              // Update thumbnails aria-current on slide change
              if (swiperInstance.thumbs && swiperInstance.thumbs.swiper) {
                const thumbsSwiper = swiperInstance.thumbs.swiper;
                const wasThumbnailFocused =
                  document.activeElement?.classList.contains(
                    'media-gallery__thumbs-button',
                  );

                thumbsSwiper.slides.forEach((thumbSlide, thumbIndex) => {
                  const button = thumbSlide.querySelector(
                    '.media-gallery__thumbs-button',
                  );
                  if (button) {
                    if (thumbIndex === this.activeIndex) {
                      button.setAttribute('aria-current', 'true');
                      if (wasThumbnailFocused) {
                        button.focus({preventScroll: true});
                      }
                    } else {
                      button.setAttribute('aria-current', 'false');
                    }
                  }
                });

                // Check if active thumb is fully visible, if not scroll to make it visible
                const activeSlide = thumbsSwiper.slides[this.activeIndex];
                if (
                  activeSlide &&
                  activeSlide.classList.contains('swiper-slide-thumb-active') &&
                  !activeSlide.classList.contains('swiper-slide-fully-visible')
                ) {
                  const containerRect = thumbsSwiper.el.getBoundingClientRect();
                  const slideRect = activeSlide.getBoundingClientRect();

                  // Calculate how much we need to scroll
                  let scrollAmount = 0;

                  // If slide is cut off on the right
                  if (slideRect.right > containerRect.right) {
                    scrollAmount = slideRect.right - containerRect.right;
                  }
                  // If slide is cut off on the left
                  else if (slideRect.left < containerRect.left) {
                    scrollAmount = slideRect.left - containerRect.left;
                  }

                  // Smoothly scroll to make it fully visible
                  if (scrollAmount !== 0) {
                    const currentTranslate = thumbsSwiper.getTranslate();
                    thumbsSwiper.setTranslate(currentTranslate - scrollAmount);
                    thumbsSwiper.navigation?.update();
                  }
                }
              }
            }
          },
          resize: function () {
            // Always move to slide 0 on resize to ensure content is visible
            this.slideTo(0, 0);
            self.updateModalOpenerSemantics(galleryType);
            self.updateSlidesListSemantics(galleryType);

            // Update thumbnail classes since thumb swiper events don't fire when disabled
            if (this.thumbs && this.thumbs.swiper) {
              const thumbsSwiper = this.thumbs.swiper;
              const mainCarousel = this;

              if (this.thumbsClassTimeout) {
                clearTimeout(this.thumbsClassTimeout);
              }

              this.thumbsClassTimeout = setTimeout(() => {
                if (mainCarousel.enabled) {
                  thumbsSwiper.el.classList.add('swiper-thumbs-enabled');
                  thumbsSwiper.el.classList.remove('swiper-thumbs-disabled');
                } else {
                  thumbsSwiper.el.classList.remove('swiper-thumbs-enabled');
                  thumbsSwiper.el.classList.add('swiper-thumbs-disabled');
                }
              }, 150);
            }
          },
          navigationNext(swiper) {
            self.ga4ArrowsNotification(swiper, 'next');
          },
          navigationPrev(swiper) {
            self.ga4ArrowsNotification(swiper, 'previous');
          },
          slideNextTransitionEnd(swiper, event) {
            self.ga4ArrowsNotification(swiper, 'swipe');
          },
          slidePrevTransitionEnd(swiper, event) {
            self.ga4ArrowsNotification(swiper, 'swipe');
          },
          paginationUpdate(swiper) {
            if (enableDots) {
              self.updatePaginationTabs(swiper);
            }
          },
        },
      };

      if (galleryType === 'columns') {
        carouselConfig.breakpoints = {
          801: {
            enabled: false,
            a11y: {
              enabled: true,
              slideRole: enableDots ? 'tabpanel' : '',
              slideLabelMessage: '',
            },
          },
        };
      }

      this.carousel = new Swiper(carouselEl, carouselConfig);

      requestAnimationFrame(() => {
        this.carousel.update();
      });

      this.fixModalButtonDrag();
      this.observeViewportForBackgroundVideos();
    }

    observeViewportForBackgroundVideos() {
      if (
        !this.hasBackgroundVideoSlides ||
        !('IntersectionObserver' in window)
      ) {
        return;
      }

      this.viewportObserver = new IntersectionObserver(
        (entries) => {
          const isVisible = entries[entries.length - 1].isIntersecting;

          if (isVisible) {
            this.updateGalleryBackgroundVideos(this.carousel);
          } else {
            customElements.whenDefined('background-video').then(() => {
              this.querySelectorAll('background-video').forEach((video) =>
                video.pause(),
              );
            });
          }
        },
        {threshold: 0},
      );
      this.viewportObserver.observe(this);
    }

    syncThumbsNavigation(thumbsSwiper, thumbIndex) {
      if (!thumbsSwiper?.enabled) return;

      thumbsSwiper.slideTo(thumbIndex, 0);
      thumbsSwiper.navigation?.update();

      requestAnimationFrame(() => {
        thumbsSwiper.navigation?.update();
      });
    }

    updateModalOpenerSemantics(galleryType) {
      const useGroupedControls =
        galleryType === 'columns' && window.innerWidth > 800;
      const modalOpeners = this.querySelectorAll(
        '[data-media-gallery-carousel] modal-opener',
      );

      modalOpeners.forEach((opener) => {
        if (useGroupedControls) {
          const slideIndex =
            opener.closest('.swiper-slide')?.dataset.slideIndex;
          opener.setAttribute('role', 'group');
          if (slideIndex)
            opener.setAttribute('aria-label', `Media ${slideIndex}`);
        } else {
          opener.removeAttribute('role');
          opener.removeAttribute('aria-label');
        }
      });
    }

    updateSlidesListSemantics(galleryType) {
      const slidesList = this.querySelector(
        '[data-media-gallery-carousel] .product__media-list',
      );
      if (!slidesList) return;

      const isCarousel = galleryType !== 'columns' || window.innerWidth <= 800;
      if (isCarousel) {
        slidesList.setAttribute('role', 'presentation');
      } else {
        slidesList.removeAttribute('role');
      }
    }

    updateGalleryBackgroundVideos(swiper) {
      if (!this.hasBackgroundVideoSlides) return;

      const activeSlide = swiper.slides[swiper.activeIndex];
      const canAutoplay =
        this.autoplayBackgroundVideo && !this.prefersReducedMotion;

      customElements.whenDefined('background-video').then(() => {
        swiper.slides.forEach((slide) => {
          const backgroundVideo = slide.querySelector('background-video');
          if (!backgroundVideo) return;

          if (slide === activeSlide) {
            if (canAutoplay && !backgroundVideo.userPaused) {
              backgroundVideo.play();
            }
          } else {
            backgroundVideo.pause();
          }
        });
      });
    }

    getPaginationTablist() {
      return this.querySelector(
        '.swiper-pagination-media-gallery-carousel[role="tablist"]',
      );
    }

    updatePaginationTabs(swiper) {
      const tablist = this.getPaginationTablist();
      if (!tablist) return;

      const tabs = [
        ...(swiper.pagination?.bullets ||
          tablist.querySelectorAll('[role="tab"]')),
      ];
      if (!tabs.length) return;

      const activeTab = tabs.find((tab) =>
        tab.classList.contains('swiper-pagination-bullet-active'),
      );
      const activeIndex = activeTab
        ? tabs.indexOf(activeTab)
        : swiper.realIndex ?? swiper.activeIndex ?? 0;

      tabs.forEach((tab, index) => {
        const isActive = index === activeIndex;
        tab.removeAttribute('aria-current');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    bindPaginationTabs(swiper) {
      const tablist = this.getPaginationTablist();
      if (!tablist || tablist.dataset.paginationKeyboardBound === 'true') {
        return;
      }

      tablist.dataset.paginationKeyboardBound = 'true';
      tablist.addEventListener(
        'keydown',
        (event) => {
          const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (!keys.includes(event.key)) return;

          const tabs = [
            ...(swiper.pagination?.bullets ||
              tablist.querySelectorAll('[role="tab"]')),
          ];
          if (!tabs.length) return;

          const currentIndex = tabs.indexOf(document.activeElement);
          if (currentIndex === -1) return;

          event.preventDefault();
          event.stopPropagation();

          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % tabs.length;
          }
          if (event.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          }
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;

          swiper.slideTo(nextIndex);

          requestAnimationFrame(() => {
            this.updatePaginationTabs(swiper);

            const activeTab = tabs.find((tab) =>
              tab.classList.contains('swiper-pagination-bullet-active'),
            );
            (activeTab || tabs[nextIndex])?.focus({preventScroll: true});
          });
        },
        true,
      );
    }

    fixModalButtonDrag() {
      const modalOpeners = this.querySelectorAll('modal-opener');
      let isDragging = false;
      let isTransitioning = false;

      this.carousel.on('touchStart', () => {
        isDragging = false;
      });

      this.carousel.on('touchMove', () => {
        isDragging = true;
      });

      this.carousel.on('touchEnd', () => {
        setTimeout(() => {
          isDragging = false;
        }, 100);
      });

      this.carousel.on('slideChangeTransitionStart', () => {
        isTransitioning = true;
      });

      this.carousel.on('slideChangeTransitionEnd', () => {
        isTransitioning = false;
      });

      // Open modal when clicking (but not dragging or transitioning)
      modalOpeners.forEach((opener) => {
        const button = opener.querySelector('button');

        opener.addEventListener('click', (e) => {
          if (!isDragging && !isTransitioning && button) {
            const modal = document.querySelector(
              opener.getAttribute('data-modal'),
            );
            if (modal) {
              modal.show(button);
            }
          }
        });

        if (button) {
          button.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        }
      });
    }

    ga4ArrowsNotification(swiper, direction) {
      const activeSlide = swiper.el.querySelector('.swiper-slide-active');
      const slideAsset = activeSlide.querySelector('.product__media img');
      const slideAssetVideo = activeSlide.querySelector(
        '.product__media video',
      );
      const item = {
        page_type: `${document.title}`,
        carousel_location: `${window.location.pathname}`,
        carousel_name: activeSlide.dataset.ga4ItemName,
        interaction_type: 'arrow_click',
        carousel_direction: direction,
        carousel_item_index: activeSlide.dataset.itemIndex,
        carousel_item_label: slideAsset?.getAttribute('alt') || '',
        media_type: slideAssetVideo ? 'video' : slideAsset ? 'image' : '',
        item_id: activeSlide.dataset.ga4ItemId,
        item_name: activeSlide.dataset.ga4ItemName,
        carousel_type: 'pdp_gallery',
      };
      CarouselClickHandleClick(item);
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
